import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  onSnapshot,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebaseConfig';
import { User } from '../services/firebaseService';

export interface Connection {
  uid: string;
  displayName: string;
  email: string;
  avatarEmoji?: string;
  /** The short status a person sets on their profile; the phone shows it on
   *  every connection card, so the web has to load it or the same person looks
   *  statusless here and not there. */
  statusText?: string;
  statusColor?: string;
}

export interface SentInvite {
  id: string;
  toEmail: string;
  status: string;
  createdAt: any;
}

interface FriendRequest {
  id: string;
  fromUserId: string;
  fromUserEmail: string;
  fromUserName: string;
  toUserId: string;
  toUserEmail: string;
  toUserName: string;
  status: string;
  read: boolean;
  fromUser?: Connection;
  toUser?: Connection;
}

interface ConnectionsContextType {
  connections: Connection[];
  pendingRequests: FriendRequest[];
  sentRequests: FriendRequest[];
  sentInvites: SentInvite[];
  hasNewRequests: boolean;
  isLoading: boolean;
  sendFriendRequest: (toEmail: string) => Promise<{ success: boolean; recipientName?: string; invited?: boolean }>;
  acceptFriendRequest: (requestId: string) => Promise<void>;
  declineFriendRequest: (requestId: string) => Promise<void>;
  cancelFriendRequest: (requestId: string) => Promise<void>;
  removeConnection: (friendId: string) => Promise<void>;
  markRequestsAsRead: () => Promise<void>;
}

// Exported so a screen can be previewed against a hand-built set of
// connections -- see src/preview/. Nothing in the app should provide this
// except ConnectionsProvider.
export const ConnectionsContext = createContext<ConnectionsContextType | null>(null);

interface ConnectionsProviderProps {
  children: React.ReactNode;
  user: User | null;
}

export const ConnectionsProvider: React.FC<ConnectionsProviderProps> = ({ children, user }) => {
  const [connections, setConnections] = useState<Connection[]>([]);
  const [pendingRequests, setPendingRequests] = useState<FriendRequest[]>([]);
  const [sentRequests, setSentRequests] = useState<FriendRequest[]>([]);
  const [sentInvites, setSentInvites] = useState<SentInvite[]>([]);
  const [hasNewRequests, setHasNewRequests] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user?.uid) {
      setConnections([]);
      setPendingRequests([]);
      setSentRequests([]);
      setSentInvites([]);
      setIsLoading(false);
      return;
    }

    const unsubscribers: (() => void)[] = [];

    const loadConnectionsData = async () => {
      try {
        setIsLoading(true);

        // Load current connections from user profile
        const userConnections = (user as any).connections || (user as any).friends || [];
        if (userConnections.length > 0) {
          const uniqueIds = [...new Set(userConnections)] as string[];
          const connectionsList: Connection[] = [];

          for (const friendId of uniqueIds) {
            try {
              const userDoc = await getDoc(doc(db, 'users', friendId));
              if (userDoc.exists()) {
                const userData = userDoc.data();
                connectionsList.push({
                  uid: friendId,
                  displayName: userData.displayName || userData.email,
                  email: userData.email,
                  avatarEmoji: userData.avatarEmoji,
                  statusText: userData.statusText,
                  statusColor: userData.statusColor,
                });
              }
            } catch (error) {
              console.error('Error loading connection:', error);
            }
          }
          setConnections(connectionsList);
        }

        // Listen to pending friend requests (received)
        const receivedQuery = query(
          collection(db, 'friendRequests'),
          where('toUserId', '==', user.uid),
          where('status', '==', 'pending')
        );

        const unsubReceived = onSnapshot(receivedQuery, async (snapshot) => {
          const requests: FriendRequest[] = [];
          for (const docSnap of snapshot.docs) {
            const data = docSnap.data();
            try {
              const senderDoc = await getDoc(doc(db, 'users', data.fromUserId));
              if (senderDoc.exists()) {
                const senderData = senderDoc.data();
                requests.push({
                  id: docSnap.id,
                  ...data,
                  fromUser: {
                    uid: data.fromUserId,
                    displayName: senderData.displayName || senderData.email,
                    email: senderData.email,
                    avatarEmoji: senderData.avatarEmoji,
                  },
                } as FriendRequest);
              }
            } catch (error) {
              console.error('Error loading request sender:', error);
            }
          }
          setPendingRequests(requests);
          setHasNewRequests(requests.some((r) => !r.read));
        });
        unsubscribers.push(unsubReceived);

        // Listen to sent friend requests
        const sentQuery = query(
          collection(db, 'friendRequests'),
          where('fromUserId', '==', user.uid),
          where('status', '==', 'pending')
        );

        const unsubSent = onSnapshot(sentQuery, async (snapshot) => {
          const requests: FriendRequest[] = [];
          for (const docSnap of snapshot.docs) {
            const data = docSnap.data();
            try {
              const recipientDoc = await getDoc(doc(db, 'users', data.toUserId));
              if (recipientDoc.exists()) {
                const recipientData = recipientDoc.data();
                requests.push({
                  id: docSnap.id,
                  ...data,
                  toUser: {
                    uid: data.toUserId,
                    displayName: recipientData.displayName || recipientData.email,
                    email: recipientData.email,
                    avatarEmoji: recipientData.avatarEmoji,
                  },
                } as FriendRequest);
              }
            } catch (error) {
              console.error('Error loading request recipient:', error);
            }
          }
          setSentRequests(requests);
        });
        unsubscribers.push(unsubSent);

        // Listen to sent email invites (to non-users)
        const invitesQuery = query(
          collection(db, 'invites'),
          where('fromUserId', '==', user.uid),
          where('status', '==', 'pending')
        );

        const unsubInvites = onSnapshot(invitesQuery, (snapshot) => {
          const invites: SentInvite[] = snapshot.docs.map((docSnap) => ({
            id: docSnap.id,
            toEmail: docSnap.data().toEmail,
            status: docSnap.data().status,
            createdAt: docSnap.data().createdAt,
          }));
          setSentInvites(invites);
        });
        unsubscribers.push(unsubInvites);

        setIsLoading(false);
      } catch (error) {
        console.error('Error loading connections:', error);
        setIsLoading(false);
      }
    };

    loadConnectionsData();

    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [user?.uid, (user as any)?.connections?.length]);

  const sendFriendRequest = useCallback(
    async (toEmail: string) => {
      if (!user?.uid || !toEmail) throw new Error('Invalid parameters');

      const usersQuery = query(collection(db, 'users'), where('email', '==', toEmail.toLowerCase()));
      const userSnapshot = await getDocs(usersQuery);

      if (userSnapshot.empty) {
        const functions = getFunctions();
        const sendInvite = httpsCallable(functions, 'sendInviteEmail');
        await sendInvite({ toEmail: toEmail.toLowerCase() });
        return { success: true, invited: true };
      }

      const recipientDoc = userSnapshot.docs[0];
      const recipientId = recipientDoc.id;
      const recipientData = recipientDoc.data();

      const userConnections = (user as any).connections || [];
      if (userConnections.includes(recipientId)) throw new Error('Already connected');

      const existingQuery = query(
        collection(db, 'friendRequests'),
        where('fromUserId', '==', user.uid),
        where('toUserId', '==', recipientId),
        where('status', '==', 'pending')
      );
      const existingSnapshot = await getDocs(existingQuery);
      if (!existingSnapshot.empty) throw new Error('Request already sent');

      const requestId = `req_${Date.now()}_${user.uid.slice(0, 8)}`;
      await setDoc(doc(db, 'friendRequests', requestId), {
        fromUserId: user.uid,
        fromUserEmail: user.email,
        fromUserName: user.displayName || user.email,
        toUserId: recipientId,
        toUserEmail: recipientData.email,
        toUserName: recipientData.displayName || recipientData.email,
        status: 'pending',
        read: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      return { success: true, recipientName: recipientData.displayName || recipientData.email };
    },
    [user]
  );

  const acceptFriendRequest = useCallback(
    async (requestId: string) => {
      const functions = getFunctions();
      const acceptFn = httpsCallable(functions, 'acceptFriendRequest');
      const result = await acceptFn({ requestId });

      const data = result.data as any;
      if (data.friend) {
        setConnections((prev) => [...prev, data.friend]);
      }
      setPendingRequests((prev) => prev.filter((r) => r.id !== requestId));
    },
    []
  );

  const declineFriendRequest = useCallback(async (requestId: string) => {
    await updateDoc(doc(db, 'friendRequests', requestId), {
      status: 'declined',
      updatedAt: serverTimestamp(),
    });
  }, []);

  const cancelFriendRequest = useCallback(async (requestId: string) => {
    await deleteDoc(doc(db, 'friendRequests', requestId));
  }, []);

  const removeConnection = useCallback(async (friendId: string) => {
    const functions = getFunctions();
    const removeFn = httpsCallable(functions, 'removeFriend');
    await removeFn({ friendId });
    setConnections((prev) => prev.filter((c) => c.uid !== friendId));
  }, []);

  const markRequestsAsRead = useCallback(async () => {
    const unread = pendingRequests.filter((r) => !r.read);
    for (const request of unread) {
      await updateDoc(doc(db, 'friendRequests', request.id), { read: true });
    }
    setHasNewRequests(false);
  }, [pendingRequests]);

  return (
    <ConnectionsContext.Provider
      value={{
        connections,
        pendingRequests,
        sentRequests,
        sentInvites,
        hasNewRequests,
        isLoading,
        sendFriendRequest,
        acceptFriendRequest,
        declineFriendRequest,
        cancelFriendRequest,
        removeConnection,
        markRequestsAsRead,
      }}
    >
      {children}
    </ConnectionsContext.Provider>
  );
};

export const useConnections = (): ConnectionsContextType => {
  const context = useContext(ConnectionsContext);
  if (!context) {
    throw new Error('useConnections must be used within a ConnectionsProvider');
  }
  return context;
};
